// app/(admin)/leads/page.tsx

import { useState, useEffect, useCallback, useRef } from "react";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import TransactionTable from "../../../components/admin/TransactionTable";
import PaginationControls from "../../../components/ui/PaginationControls";
import Button from "../../../components/ui/Button";
import { getAdminChannels, getAdminChannelLeads } from "../../../lib/apiClient";
import {
  TransactionAdminResponse,
  PopulatedChannel,
  Channel,
} from "../../../types";
import { getErrorMessage, formatDate } from "../../../lib/utils";
import toast from "react-hot-toast";
import EmptyState from "../../../components/ui/EmptyState";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv } from "@fortawesome/free-solid-svg-icons";

const ITEMS_PER_PAGE = 15;

export default function ChannelSubscriptionsPage() {
  const [channels, setChannels] = useState<
    Channel[] | PopulatedChannel[]
  >([]);
  const [transactions, settransactions] = useState<TransactionAdminResponse[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isChannelsLoading, setIsChannelsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [channelId, setchannelId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const formatDefaultDate = (date: Date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>(
    formatDefaultDate(firstDayOfMonth)
  );
  const [endDate, setEndDate] = useState<string>(
    formatDefaultDate(lastDayOfMonth)
  );

  // Track whether initial fetch has happened
  const hasFetched = useRef(false);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  // Fetch Channel Details - runs once
  const fetchChannels = useCallback(async () => {
    setIsChannelsLoading(true);
    try {
      const data = await getAdminChannels();
      setChannels(data);
    } catch (err) {
      const message = getErrorMessage(err);
      toast.error(`Error loading channels: ${message}`, {
        id: "fetch-channels-error",
      });
    } finally {
      setIsChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, []);

  // Fetch transactions with server-side pagination & filtering
  const fetchTransactions = useCallback(async (
    page: number,
    channel?: string,
    start?: string,
    end?: string,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: {
        page: number;
        limit: number;
        channel_id?: string;
        start_date?: string;
        end_date?: string;
      } = { page, limit: ITEMS_PER_PAGE };

      if (channel) filters.channel_id = channel;
      if (start) filters.start_date = start;
      if (end) filters.end_date = end;

      const data = await getAdminChannelLeads(filters);
      settransactions(data.transactions);
      setTotalItems(data.total);
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(`Error loading transactions: ${msg}`, {
        id: "fetch-subs-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount and when page or channel changes
  useEffect(() => {
    fetchTransactions(currentPage, channelId || undefined, startDate, endDate);
    hasFetched.current = true;
  }, [currentPage, channelId]);

  // Apply filter button handler
  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchTransactions(1, channelId || undefined, startDate, endDate);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChannelChange = (value: string) => {
    setchannelId(value);
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (!transactions.length) return;

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Channel Name",
      "Plan Name",
      "Type",
      "Amount",
      "End Date",
      "Created At",
    ];

    const rows = transactions.map((sub) => {
      return [
        sub.user_id?.name || "N/A",
        sub.user_id?.email || "N/A",
        sub.user_id?.phone || "N/A",
        typeof sub.channel_id === "object" && sub.channel_id !== null
          ? sub.channel_id.name
          : typeof sub.channel_id === "string"
            ? sub.channel_id
            : "N/A",
        typeof sub.plan_id === "object" && sub.plan_id !== null
          ? sub.plan_id.name
          : typeof sub.plan_id === "string"
            ? sub.plan_id
            : "N/A",
        sub.type,
        sub.amount,
        sub.subscription_id &&
          typeof sub.subscription_id === "object" &&
          sub.subscription_id.end_date
          ? formatDate(sub.subscription_id.end_date)
          : "-",
        formatDate(sub.createdAt),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "leads_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSubscriptionContent = () => {
    if (isLoading && transactions.length === 0) {
      return (
        <div className="flex justify-center items-center min-h-[200px]">
          <LoadingIndicator text="Loading transactions..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-4">
          <ErrorMessage title="Error Loading transactions" message={error} />
          <Button onClick={handleApplyFilter} className="mt-4">
            Retry Load
          </Button>
        </div>
      );
    }

    if (!isLoading && transactions.length === 0) {
      const message =
        startDate || endDate || channelId
          ? "No transactions match the current filters."
          : "No transactions found for this channel yet.";
      return <EmptyState message={message}></EmptyState>;
    }

    return (
      <>
        <TransactionTable transactions={transactions} isLoading={isLoading} />
        <PaginationControls
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={handlePageChange}
          className="px-4 py-3"
        />
      </>
    );
  };

  return (
    <div>
      <div className="mb-6 rounded-lg shadow bg-dark-secondary border border-dark-tertiary">
        <div className="px-4 pt-4">
          <h2 className="text-lg font-semibold text-white mb-4">
            Transaction Date Filter
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-4 px-4 pb-6">
          <Input
            label="Start Date"
            id="start-date"
            name="start-date"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            containerClassName="mb-0"
            disabled={isLoading}
          />

          <Input
            label="End Date"
            id="end-date"
            name="end-date"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            containerClassName="mb-0"
            disabled={isLoading}
          />

          <Button
            onClick={handleApplyFilter}
            disabled={isLoading}
          >
            Apply Filter
          </Button>
        </div>
        <div className="px-4 pt-4">
          <h2 className="text-lg font-semibold text-white mb-4">
            Channel Filter
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-4 px-4 pb-6">
          <Select
            id="channel"
            name="channel"
            label="Channel"
            placeholder="Select Channel"
            value={channelId}
            onChange={(e) => handleChannelChange(e.target.value)}
            options={[{ _id: "", name: "All Channels" }, ...channels].map(
              (channel) => ({
                value: channel._id,
                label: channel.name,
              })
            )}
            className="bg-dark-secondary text-text-primary"
            containerClassName=""
          />
          {transactions.length > 0 && (
            <Button
              onClick={handleExportCSV}
              variant="primary"
              className="w-full sm:w-auto ml-2"
              size="md"
            >
              <FontAwesomeIcon icon={faFileCsv} /> Export To CSV
            </Button>
          )}
        </div>
      </div>

      {/* Table and Pagination Section */}
      <div className="bg-dark-secondary rounded-lg shadow-md overflow-hidden">
        {renderSubscriptionContent()}
      </div>
    </div>
  );
}
